import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, EditableToken } from '@vibesites/runtime/react';
import { FeeGuideEmbed } from './FeeGuideEmbed';

const FEES_HASH = '#fees';

function scrollToFeesSection() {
  if (typeof window === 'undefined') return;
  if (window.location.hash !== FEES_HASH) return;
  const el = document.getElementById('fees');
  if (!el) return;
  // Defer one frame so layout (hero / embed shell) is ready.
  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/**
 * Standalone section that renders the property fee guide inline (the MarketApts
 * embed). Shares the embed implementation with the HeroSolidBG secondary-CTA
 * popup via FeeGuideEmbed / FeeGuideModal. Sits tight beneath the hero — only a
 * small top gap keeps it clear of the hero box; there is no visible heading.
 *
 * Anchor id=`fees` — Community Info "Property Fee Guide" links to `/apply-now#fees`.
 */
export function FeeGuide() {
  // Editor-only override for which community's fees the embed shows. Defaults
  // to the property's AIM code; surfaced as a hidden control (config, not
  // display copy) so the section stays fully embed-driven.
  const aimCode = useContentToken('feeguide.aim_code');

  useEffect(() => {
    scrollToFeesSection();
    window.addEventListener('hashchange', scrollToFeesSection);
    return () => window.removeEventListener('hashchange', scrollToFeesSection);
  }, []);

  return (
    <section id="fees" className="bg-white pt-4 md:pt-6 pb-8 md:pb-12 scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="feeguide.aim_code"
          type="text"
          label="AIM Property Code"
          description="MarketApts AIM code selecting which community's fees to show. Leave blank to use the property's custom_aim_property_code field."
          as="span"
          className="sr-only"
        >
          {aimCode || ''}
        </EditableToken>

        <FeeGuideEmbed aimCode={aimCode ? String(aimCode) : undefined} />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeGuide',
  displayName: 'Fee Guide',
  tokens: {
    'feeguide.aim_code': {
      label: 'AIM Property Code',
      type: 'text',
      description:
        "MarketApts AIM code selecting which community's fees to show. Leave blank to use the property's custom_aim_property_code field.",
      defaultValue: '',
    },
  },
};

(FeeGuide as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeGuide;
